import React, { useState } from 'react';
import { 
  Box, 
  List, 
  ListItem, 
  ListItemButton, 
  ListItemIcon, 
  ListItemText, 
  Typography, 
  Divider, 
  Chip, 
  Collapse,
  Drawer,
  IconButton,
  AppBar,
  Toolbar,
  useMediaQuery,
  useTheme,
  Menu,
  MenuItem,
  Avatar,
  Skeleton,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PaymentIcon from '@mui/icons-material/Payment';
import ReceiptIcon from '@mui/icons-material/Receipt';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SettingsIcon from '@mui/icons-material/Settings';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import StoreIcon from '@mui/icons-material/Store';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import WorkIcon from '@mui/icons-material/Work';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import CategoryIcon from '@mui/icons-material/Category';
import BusinessIcon from '@mui/icons-material/Business';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CheckIcon from '@mui/icons-material/Check';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import { useNavigate, useLocation } from 'react-router-dom';
import { UserButton, useUser } from '@clerk/clerk-react';
import { useOrganization } from './providers/OrganizationProvider';
import { usePermissions } from './hooks/usePermissions';
import { PERMISSIONS } from './utils/permissions';

const DRAWER_WIDTH = 260;

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUser();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { can, isAdmin, isManager } = usePermissions();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(
    location.pathname.startsWith('/settings')
  );
  const [orgMenuAnchor, setOrgMenuAnchor] = useState(null);
  
  // Organization context
  const { 
    organization, 
    organizations, 
    switchOrganization, 
    hasMultipleOrgs,
    isLoading: orgLoading 
  } = useOrganization();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSettingsClick = () => {
    setSettingsOpen(!settingsOpen);
    if (!settingsOpen) {
      navigate('/settings/positions');
    }
  };

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  // Settings sub-items with permissions (admin+ only for settings)
  const allSettingsSubItems = [
    { text: 'Ωράριο Καταστήματος', icon: <StoreIcon />, path: '/settings/store-hours', permission: PERMISSIONS.SETTINGS_VIEW, hidden: true },
    { text: 'Απαιτήσεις Προσωπικού', icon: <PersonSearchIcon />, path: '/settings/staffing-requirements', permission: PERMISSIONS.SETTINGS_VIEW, hidden: true },
    { text: 'Θέσεις Εργασίας', icon: <WorkIcon />, path: '/settings/positions', permission: PERMISSIONS.SETTINGS_VIEW },
    { text: 'Προκαθορισμένες Ώρες', icon: <AccessTimeIcon />, path: '/settings/preset-hours', permission: PERMISSIONS.SETTINGS_VIEW },
    { text: 'Κατηγορίες', icon: <CategoryIcon />, path: '/settings/categories', permission: PERMISSIONS.SETTINGS_VIEW },
    { text: 'myDATA Integration', icon: <IntegrationInstructionsIcon />, path: '/settings/mydata', permission: PERMISSIONS.SETTINGS_MYDATA },
    { text: 'Οργανισμός & Μέλη', icon: <PeopleIcon />, path: '/settings/organization', permission: PERMISSIONS.ORG_MEMBERS_VIEW },
    { text: 'Συνδρομή', icon: <CreditCardIcon />, path: '/settings/billing', permission: PERMISSIONS.ORG_MEMBERS_VIEW },
  ];
  
  // Filter settings based on permissions and visibility
  const settingsSubItems = allSettingsSubItems.filter(item => !item.hidden && (!item.permission || can(item.permission)));

  // Menu items with permissions
  const allMenuItems = [
    { text: 'Επισκόπηση', icon: <DashboardIcon />, path: '/', permission: PERMISSIONS.DASHBOARD_VIEW },
    { text: 'Προσωπικό', icon: <PeopleIcon />, path: '/staff', permission: PERMISSIONS.EMPLOYEES_VIEW },
    { text: 'Πρόγραμμα', icon: <CalendarTodayIcon />, path: '/schedule', permission: PERMISSIONS.SHIFTS_VIEW },
    // Hidden but not deleted - may be needed later:
    // { text: 'Πρόγραμμα 2', icon: <CalendarTodayIcon />, path: '/schedule2', permission: PERMISSIONS.SHIFTS_VIEW },
    // { text: 'Πρόγραμμα 3', icon: <CalendarTodayIcon />, path: '/schedule3', permission: PERMISSIONS.SHIFTS_VIEW },
    // { text: 'Αμοιβές', icon: <AttachMoneyIcon />, path: '/salaries', permission: PERMISSIONS.SALARIES_VIEW },
    { text: 'Πληρωμές', icon: <PaymentIcon />, path: '/payments', permission: PERMISSIONS.SALARIES_VIEW },
    { text: 'Έξοδα', icon: <ReceiptIcon />, path: '/expenses', permission: PERMISSIONS.EXPENSES_VIEW },
    { text: 'Έσοδα', icon: <TrendingUpIcon />, path: '/revenue', permission: PERMISSIONS.REVENUE_VIEW },
    { text: 'Οικονομική Εικόνα', icon: <AttachMoneyIcon />, path: '/financial-overview', permission: PERMISSIONS.FINANCIAL_VIEW },
  ];
  
  // Filter menu items based on permissions
  const menuItems = allMenuItems.filter(item => !item.permission || can(item.permission));

  // Sidebar content - shared between desktop and mobile
  const sidebarContent = (
    <Box
      sx={{
        width: DRAWER_WIDTH,
        height: '100%',
        backgroundColor: '#f1f2f3',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Organization Switcher */}
      <Box sx={{ p: 2, pb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box 
          onClick={hasMultipleOrgs ? (e) => setOrgMenuAnchor(e.currentTarget) : undefined}
          sx={{ 
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            cursor: hasMultipleOrgs ? 'pointer' : 'default',
            borderRadius: 2,
            p: 1,
            mx: -1,
            '&:hover': hasMultipleOrgs ? {
              backgroundColor: '#e8e8e9',
            } : {},
          }}
        >
          {orgLoading ? (
            <>
              <Skeleton variant="rounded" width={40} height={40} />
              <Box sx={{ flex: 1 }}>
                <Skeleton width={100} height={20} />
                <Skeleton width={60} height={14} />
              </Box>
            </>
          ) : (
            <>
              <Avatar 
                sx={{ 
                  width: 40, 
                  height: 40, 
                  bgcolor: 'primary.main',
                  fontSize: '1rem',
                  fontWeight: 600,
                }}
              >
                {organization?.name?.charAt(0) || 'P'}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 600, 
                    color: '#303030',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {organization?.name || 'Οργανισμός'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {organization?.role === 'owner' ? 'Ιδιοκτήτης' : 
                     organization?.role === 'admin' ? 'Διαχειριστής' : 
                     organization?.role === 'manager' ? 'Υπεύθυνος' : 'Μέλος'}
                  </Typography>
                  {(organization?.plan === 'pro' || organization?.plan === 'business') && organization?.plan_status !== 'trialing' && (
                    <Chip 
                      label={organization?.plan === 'business' ? 'BUSINESS' : 'PRO'} 
                      size="small" 
                      sx={{ 
                        height: 16, 
                        fontSize: '0.6rem', 
                        fontWeight: 700,
                        bgcolor: '#f5a623', 
                        color: 'white',
                        '& .MuiChip-label': { px: 0.5 }
                      }} 
                    />
                  )}
                </Box>
              </Box>
              {hasMultipleOrgs && (
                <UnfoldMoreIcon sx={{ color: 'text.secondary', fontSize: '1.25rem' }} />
              )}
            </>
          )}
        </Box>
        {isMobile && (
          <IconButton 
            onClick={handleDrawerToggle}
            sx={{ 
              color: '#616161',
              mt: -0.5,
              mr: -1,
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </Box>
      
      {/* Organization Menu */}
      <Menu
        anchorEl={orgMenuAnchor}
        open={Boolean(orgMenuAnchor)}
        onClose={() => setOrgMenuAnchor(null)}
        PaperProps={{
          sx: {
            width: 240,
            maxHeight: 300,
            mt: 1,
          }
        }}
      >
        <Typography variant="caption" sx={{ px: 2, py: 1, color: 'text.secondary', display: 'block' }}>
          Αλλαγή Οργανισμού
        </Typography>
        {organizations.map((org) => (
          <MenuItem
            key={org.id}
            onClick={() => {
              switchOrganization(org.id);
              setOrgMenuAnchor(null);
              // Reload to refresh all data
              window.location.reload();
            }}
            sx={{
              py: 1.5,
              px: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <Avatar 
              sx={{ 
                width: 32, 
                height: 32, 
                bgcolor: org.id === organization?.id ? 'primary.main' : '#9E9E9E',
                fontSize: '0.875rem',
              }}
            >
              {org.name?.charAt(0)}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: org.id === organization?.id ? 600 : 400 }}>
                {org.name}
              </Typography>
            </Box>
            {org.id === organization?.id && (
              <CheckIcon sx={{ color: 'primary.main', fontSize: '1.25rem' }} />
            )}
          </MenuItem>
        ))}
      </Menu>

      <Divider />

      {/* Main Menu - Scrollable */}
      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        <List sx={{ pt: 2, px: 1.5, pb: 0 }}>
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  onClick={() => handleNavigation(item.path)}
                  sx={{
                    borderRadius: 2,
                    py: 1.25,
                    px: 1.5,
                    backgroundColor: isActive ? '#ffffff' : 'transparent',
                    color: isActive ? '#303030' : '#616161',
                    '&:hover': {
                      backgroundColor: isActive ? '#ffffff' : '#e8e8e9',
                    },
                    '& .MuiListItemIcon-root': {
                      color: isActive ? '#303030' : '#616161',
                      minWidth: 40,
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText 
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span>{item.text}</span>
                        {item.badge && (
                          <Chip 
                            label={item.badge} 
                            color={item.badgeColor || 'primary'} 
                            size="small" 
                            sx={{ 
                              height: '18px', 
                              fontSize: '0.65rem',
                              fontWeight: 700,
                            }} 
                          />
                        )}
                      </Box>
                    }
                    primaryTypographyProps={{
                      fontSize: '0.875rem',
                      fontWeight: isActive ? 600 : 500,
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Box>

      <Divider />

      {/* Settings at bottom - Scrollable */}
      <Box sx={{ overflowY: 'auto', overflowX: 'hidden', maxHeight: '40vh' }}>
        <List sx={{ px: 1.5, pb: 2 }}>
          <ListItem disablePadding>
            <ListItemButton
              onClick={handleSettingsClick}
              sx={{
                borderRadius: 2,
                py: 1.25,
                px: 1.5,
                backgroundColor: settingsOpen ? '#ffffff' : 'transparent',
                color: settingsOpen ? '#303030' : '#616161',
                '&:hover': {
                  backgroundColor: settingsOpen ? '#ffffff' : '#e8e8e9',
                },
                '& .MuiListItemIcon-root': {
                  color: settingsOpen ? '#303030' : '#616161',
                  minWidth: 40,
                },
              }}
            >
              <ListItemIcon>
                <SettingsIcon />
              </ListItemIcon>
              <ListItemText 
                primary="Ρυθμίσεις"
                primaryTypographyProps={{
                  fontSize: '0.875rem',
                  fontWeight: settingsOpen ? 600 : 500,
                }}
              />
              {settingsOpen ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
          </ListItem>
          
          <Collapse in={settingsOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding sx={{ pl: 3, pb: 1 }}>
              {settingsSubItems.map((subItem) => {
                const isActive = location.pathname === subItem.path;
                return (
                  <ListItem key={subItem.text} disablePadding sx={{ mb: 0.5 }}>
                    <ListItemButton
                      onClick={() => handleNavigation(subItem.path)}
                      sx={{
                        borderRadius: 2,
                        py: 0.75,
                        px: 1.5,
                        backgroundColor: isActive ? '#f0f0f0' : 'transparent',
                        color: isActive ? '#303030' : '#616161',
                        '&:hover': {
                          backgroundColor: isActive ? '#f0f0f0' : '#e8e8e9',
                        },
                        '& .MuiListItemIcon-root': {
                          color: isActive ? '#303030' : '#616161',
                          minWidth: 36,
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {subItem.icon}
                      </ListItemIcon>
                      <ListItemText 
                        primary={subItem.text}
                        primaryTypographyProps={{
                          fontSize: '0.8125rem',
                          fontWeight: isActive ? 600 : 500,
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Collapse>
        </List>
      </Box>

      {/* Upgrade CTA for Free Users */}
      {organization?.plan === 'free' && organization?.plan_status !== 'trialing' && (
        <Box sx={{ 
          mx: 2, 
          mb: 2,
          p: 2,
          borderRadius: 2,
          background: 'linear-gradient(135deg, #008060 0%, #00a67d 100%)',
          cursor: 'pointer',
          transition: 'transform 0.2s',
          '&:hover': { transform: 'scale(1.02)' },
        }}
        onClick={() => navigate('/settings/billing')}
        >
          <Typography variant="body2" sx={{ color: 'white', fontWeight: 600, mb: 0.5 }}>
            ⭐ Αναβάθμιση πλάνου
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
            Pro, Business, myDATA & AI Assistant
          </Typography>
        </Box>
      )}

      {/* User Section */}
      <Box sx={{ 
        mt: 'auto', 
        p: 2, 
        borderTop: (theme) => `1px solid ${theme.palette.divider}`, 
        display: 'flex', 
        alignItems: 'center',
        gap: 1.5
      }}>
        <UserButton afterSignOutUrl="/sign-in" />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography 
            variant="body2" 
            sx={{ 
              fontWeight: 600, 
              color: '#303030',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {user?.firstName || user?.emailAddresses?.[0]?.emailAddress?.split('@')[0] || 'User'}
          </Typography>
          <Typography variant="caption" sx={{ color: '#616161' }}>
            v1.2 Billing
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Mobile App Bar */}
      {isMobile && (
        <AppBar 
          position="fixed" 
          sx={{ 
            backgroundColor: '#f1f2f3',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            zIndex: theme.zIndex.drawer + 1,
          }}
        >
          <Toolbar sx={{ minHeight: '56px !important' }}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ color: '#303030' }}
            >
              <MenuIcon />
            </IconButton>
            <Typography 
              variant="h6" 
              noWrap 
              component="div"
              sx={{ 
                flexGrow: 1, 
                color: 'primary.main',
                fontWeight: 700,
                letterSpacing: '-0.02em',
              }}
            >
              {organization?.name || 'Majestify'}
            </Typography>
            <UserButton afterSignOutUrl="/sign-in" />
          </Toolbar>
        </AppBar>
      )}

      {/* Mobile Drawer */}
      {isMobile ? (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: DRAWER_WIDTH,
              border: 'none',
            },
          }}
        >
          {sidebarContent}
        </Drawer>
      ) : (
        /* Desktop Sidebar */
        <Box
          sx={{
            width: DRAWER_WIDTH,
            height: '100vh',
            borderRight: (theme) => `1px solid ${theme.palette.divider}`,
            position: 'fixed',
            left: 0,
            top: 0,
          }}
        >
          {sidebarContent}
        </Box>
      )}
    </>
  );
};

export { DRAWER_WIDTH };
export default Sidebar;
